// src/components/survey/pre-drive-survey.tsx
'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { usePreRegEvent, useSubmitSurvey } from '@/hooks';
import type { EventType } from '@/types';
import { initializeSurveyForBrand } from '@latitude-digital/survey-core';
import { mapToLincolnPayload } from '@latitude-digital/survey-core/mappers';
import { AlertCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { FunctionFactory, Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import surveyJSONRejuvenate from './json/pre-rejuvenate.json';
import surveyJSONTestDrive from './json/pre-test-drive.json';

const surveyJSON: Record<EventType, Record<string, unknown>> = {
  rejuvenate_only: surveyJSONRejuvenate,
  test_drive_rejuvenate: surveyJSONTestDrive,
};

// Register email validator — the email question type uses expression
// `{email} empty or validateEmail({email})` which requires this function.
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validateEmail(params: unknown[]): boolean {
  const email = params[0];
  if (!email || typeof email !== 'string') return true;
  return emailRegex.test(email);
}
FunctionFactory.Instance.register('validateEmail', validateEmail, false);

export function PreDriveSurvey() {
  const router = useRouter();

  // Extract hash and optional event_portal from the browser URL
  // Route: /pre/[hash]/[[...event_portal]]
  const [hash, setHash] = useState<string | null>(null);
  const [eventPortal, setEventPortal] = useState<string | undefined>();

  useEffect(() => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    // segments: ['pre', hash] or ['pre', hash, event_portal]
    const extractedHash = segments[1] ?? null;
    if (extractedHash && extractedHash !== 'placeholder') {
      setHash(extractedHash); // eslint-disable-line react-hooks/set-state-in-effect
    }
    const extractedPortal = segments[2];
    if (extractedPortal) {
      setEventPortal(extractedPortal);  
    }
  }, []);

  // Fetch event info (event_id + event_name) by pre-registration hash
  const { data: eventData, isLoading, isError, error } = usePreRegEvent(hash ?? '');

  // Build redirect paths
  const eventTypeParam = eventData?.event_type ? `?event_type=${eventData.event_type}` : '';
  const successPath = eventPortal
    ? `/submit/success/${eventPortal}${eventTypeParam}`
    : `/submit/success${eventTypeParam}`;
  const errorPath = eventPortal ? `/submit/error/${eventPortal}` : '/submit/error';

  const accessKey = process.env.NEXT_PUBLIC_GTB_ACCESS_KEY;

  console.log('accessKey', accessKey);

  // Survey submission mutation
  const submitSurvey = useSubmitSurvey(accessKey ?? '', {
    onSuccess: () => router.push(successPath),
    onError: () => router.push(errorPath),
  });

  // Create SurveyJS model
  const survey = useMemo(() => {
    const model = new Model(surveyJSON[eventData?.event_type ?? 'rejuvenate_only']);

    initializeSurveyForBrand(model, { brand: 'Lincoln' });

    // Required for FDS radio button styling
    (model as Record<string, unknown>).__isFDSBrand = true;

    model.showCompletedPage = false;
    model.focusFirstQuestionAutomatic = false;
    model.questionErrorLocation = 'bottom';
    model.widthMode = 'responsive';

    // Inject metadata into survey data when survey first renders
    model.onAfterRenderSurvey.add((sender) => {
      sender.mergeData({
        start_time: new Date().toISOString(),
        survey_date: new Date().toISOString(),
        event_id: String(eventData?.event_id ?? ''),
        app_version: 'surveyjs_2.0',
        device_survey_guid: crypto.randomUUID(),
        abandoned: 0,
        _utm: new URLSearchParams(window.location.search).toString(),
        _referrer: window.frames?.top?.document?.referrer,
        language: sender.locale || 'en',
        _language: window.navigator?.language,
        _screenWidth: window.screen?.width,
        _offset: new Date().getTimezoneOffset(),
        _timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    });

    // Wire up Google Places autocomplete on the address1 field
    model.onAfterRenderQuestion.add((_sender, options) => {
      if (options.question.name !== 'address1') return;
      if (typeof google === 'undefined' || !google.maps?.places) return;

      const input = options.htmlElement.querySelector('input');
      if (!input || input.dataset.autocompleteInitialized) return;
      input.dataset.autocompleteInitialized = 'true';

      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: ['us'] },
        fields: ['address_components', 'formatted_address'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;

        const get = (type: string, short = false) =>
          place.address_components?.find((c) => c.types.includes(type))?.[
            short ? 'short_name' : 'long_name'
          ] ?? '';

        const streetNumber = get('street_number');
        const route = get('route');
        const parsedData: Record<string, string> = {
          address1: `${streetNumber} ${route}`.trim(),
          address2: get('subpremise'),
          city: get('locality'),
          state: get('administrative_area_level_1', true),
          zip_code: get('postal_code'),
          country: get('country', true),
        };

        // Find the composite panel question containing address1.
        // Panel child values must be set on the child elements directly,
        // not via model.setValue() which targets the top-level data hash.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- SurveyJS Question types lack composite panel typings
        const compositeQ = model.getAllQuestions().find((q: Record<string, any>) =>
          q.contentPanel?.elements?.some((e: Record<string, unknown>) => e.name === 'address1')
        );

        if (compositeQ) {
          const fieldMapping: Record<string, string> = {
            address1: 'address1',
            address2: 'address2',
            city: 'city',
            state: 'state',
            zip: 'zip_code',     // element name 'zip' → data key 'zip_code'
            country: 'country',
          };

          Object.entries(fieldMapping).forEach(([elementName, dataKey]) => {
            const child = compositeQ.getQuestionByName(elementName);
            if (child) {
              child.value = parsedData[dataKey];
            }
          });
        } else {
          // Fallback for non-composite address fields
          model.setValue('address1', parsedData.address1);
          model.setValue('address2', parsedData.address2);
          model.setValue('city', parsedData.city);
          model.setValue('state', parsedData.state);
          model.setValue('zip_code', parsedData.zip_code);
        }
      });
    });

    return model;
  }, [eventData?.event_type, eventData?.event_id]);

  // Set event_name variable from API response
  useEffect(() => {
    if (eventData?.event_name && survey) {
      survey.setVariable('event_name', eventData.event_name);
    }
  }, [eventData, survey]);

  // Handle survey completion
  useEffect(() => {
    const onComplete = (sender: Model) => {
      sender.mergeData({ end_time: new Date().toISOString() });

      const payload = mapToLincolnPayload(sender, sender.data, {
        lincolnEventID: String(eventData?.event_id ?? ''),
        surveyType: 'preTD',
      });

      payload.custom_data = JSON.stringify({
        _utm: payload._utm,
        _referrer: payload._referrer,
        _language: payload._language,
        _screenWidth: payload._screenWidth,
        _offset: payload._offset,
        _timeZone: payload._timeZone,
      });

      submitSurvey.mutate(payload);
    };
    survey.onComplete.add(onComplete);
    return () => survey.onComplete.remove(onComplete);
  }, [survey, eventData?.event_id, submitSurvey]);

  if (!hash || isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !eventData) {
    return (
      <div className="mx-auto max-w-2xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message || 'Unable to load survey. Please check the URL and try again.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (submitSurvey.isPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="text-center space-y-4">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
          <p className="text-muted-foreground">Submitting your survey...</p>
        </div>
      </div>
    );
  }

  return (
    <div id="fd-nxt" className="lincoln_light">
      <Survey model={survey} />
    </div>
  );
}
